import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getCoachees, deleteCoachee } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Search, 
  Loader2, 
  Eye, 
  Trash2, 
  Edit,
  Mail,
  Phone,
  Briefcase
} from 'lucide-react';

export default function CoacheesList() {
  const [coachees, setCoachees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCoachees();
  }, []);

  const loadCoachees = async () => {
    try {
      const data = await getCoachees();
      setCoachees(data);
    } catch (error) {
      toast.error('Error al cargar coachees');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (coacheeId, name) => {
    try {
      await deleteCoachee(coacheeId);
      toast.success(`${name} eliminado exitosamente`);
      loadCoachees();
    } catch (error) {
      toast.error('Error al eliminar coachee');
    }
  };

  const filteredCoachees = coachees.filter(coachee => 
    coachee.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coachee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coachee.objetivos_generales?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="coachees-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-heading text-3xl text-slate-800">
              Mis Coachees
            </h1>
            <p className="text-slate-500 mt-1">
              {coachees.length} coachee{coachees.length !== 1 ? 's' : ''} registrado{coachees.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Link to="/coachees/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="add-coachee-btn">
              <UserPlus className="w-4 h-4" />
              Nuevo Coachee
            </Button>
          </Link>
        </div>

        {/* Search */}
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, email u objetivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 glass-input"
                data-testid="search-coachees-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Coachees Grid */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-10 h-10 animate-spin text-blue-900 mx-auto" />
            <p className="text-slate-500 mt-3">Cargando coachees...</p>
          </div>
        ) : filteredCoachees.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-heading text-xl text-slate-700 mb-2">
                {searchTerm ? 'No se encontraron resultados' : 'No tienes coachees'}
              </h3>
              <p className="text-slate-500 mb-6">
                {searchTerm ? 'Intenta con otra búsqueda' : 'Comienza agregando tu primer coachee'}
              </p>
              {!searchTerm && (
                <Link to="/coachees/new">
                  <Button className="btn-secondary">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Agregar Coachee
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-stagger">
            {filteredCoachees.map((coachee) => (
              <Card 
                key={coachee.id_coachee} 
                className="glass-card card-hover overflow-hidden"
                data-testid={`coachee-card-${coachee.id_coachee}`}
              >
                {/* Header with Avatar */}
                <div className="bg-gradient-to-r from-blue-900 to-sky-600 p-6 text-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-2xl font-bold">
                        {coachee.nombre?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{coachee.nombre}</h3>
                        <p className="text-white/80 text-sm">
                          Desde {coachee.fecha_creacion || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <CardContent className="p-6 space-y-4">
                  {coachee.email && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-sm truncate">{coachee.email}</span>
                    </div>
                  )}
                  {coachee.telefono && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{coachee.telefono}</span>
                    </div>
                  )}
                  {coachee.info_laboral && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Briefcase className="w-4 h-4 text-slate-400" />
                      <span className="text-sm truncate">{coachee.info_laboral}</span>
                    </div>
                  )}
                  {coachee.objetivos_generales && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="label-uppercase mb-1">Objetivo</p>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {coachee.objetivos_generales}
                      </p>
                    </div>
                  )}
                </CardContent>

                {/* Actions */}
                <div className="px-6 pb-6 flex gap-2">
                  <Link to={`/coachees/${coachee.id_coachee}`} className="flex-1">
                    <Button className="w-full btn-primary" data-testid={`view-coachee-${coachee.id_coachee}`}>
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Perfil
                    </Button>
                  </Link>
                  <Link to={`/coachees/${coachee.id_coachee}/edit`}>
                    <Button variant="outline" className="border-slate-200 hover:bg-slate-50">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="glass-card">
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar coachee?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción eliminará permanentemente a {coachee.nombre} y todas sus sesiones asociadas.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="btn-outline">Cancelar</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDelete(coachee.id_coachee, coachee.nombre)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
